import { AcceptMultiplierChanges, defaultUserSettings } from '@/hooks/use-auth-user-settings';
import { isOptionOpen, isProducerDown } from '@/utils/option-status';

import { BetTypes, ComboBetTypes, SbkBetSlip } from '../types';
import {
    BetIds,
    calculatePayout,
    generateSgpOddsId,
    getComboBetType,
    getComboOdds,
    getProfitBoostAddedProfit,
    groupSelectionIdsByEvent,
    hasAnyComboSelectionOddsChanged,
    hasAnySgpOddsChanged,
    isBetBetrBucks,
    isBetValid,
    isComboBetSelectionsValid,
} from './betslip-utils';

export const validBetsSelector = (state: SbkBetSlip) => Object.values(state.bets).filter(bet => isBetValid(bet, state));

export const betSlipInitialTabSelector = (state: Pick<SbkBetSlip, 'selectionOrder' | 'closedSelections'>) =>
    state.selectionOrder.length - state.closedSelections.length > 1 ? 'Parlays' : 'Singles';

export const totalStakeSelector = (state: SbkBetSlip) =>
    validBetsSelector(state).reduce((acc, bet) => acc + (bet.stake ?? 0), 0);

export const totalCashStakeSelector = (state: SbkBetSlip) =>
    validBetsSelector(state).reduce((acc, bet) => acc + (!state.isBetrBucksEnabled[bet.id] ? bet.stake ?? 0 : 0), 0);

export const totalBetrBucksStakeSelector = (state: SbkBetSlip) =>
    validBetsSelector(state).reduce((acc, bet) => acc + (state.isBetrBucksEnabled[bet.id] ? bet.stake ?? 0 : 0), 0);

export const totalPayoutSelector = (isCashCurrency: boolean) => (state: SbkBetSlip) =>
    validBetsSelector(state).reduce((acc, bet) => {
        const payout = calculatePayout(bet, state, isCashCurrency);
        return acc + payout;
    }, 0);

export const betPayoutSelector = (betId: string, isCashCurrency: boolean) => (state: SbkBetSlip) => {
    const bet = state.bets[betId];
    if (!bet) {
        return 0;
    }
    return calculatePayout(bet, state, isCashCurrency);
};

export const betProfitBoostSelector = (betId: string, isCashCurrency: boolean) => (state: SbkBetSlip) => {
    const bet = state.bets[betId];
    if (!bet) {
        return 0;
    }
    return getProfitBoostAddedProfit(bet, state, isCashCurrency);
};

export const activeSingleBetsSelector = (state: SbkBetSlip) =>
    validBetsSelector(state).filter(bet => bet.betType === BetTypes.Single && bet.stake);

export const hasActiveParlaySelector = (state: SbkBetSlip): boolean => {
    const bet = state.bets[BetIds.COMBO];
    return !!bet && isComboBetSelectionsValid(state) && !!bet.stake;
};

export const hasSoccerSelectionSelector = (state: SbkBetSlip): boolean =>
    state.selectionOrder.some(selectionId => {
        const eventId = state.selections[selectionId]?.eventId;
        return !!eventId && state.events[eventId]?.sport.name.toLowerCase() === 'soccer';
    });

export const hasStakeErrorSelector = (state: SbkBetSlip): boolean => {
    return (
        state.totalStakeErrors.length > 0 || validBetsSelector(state).some(bet => !!bet.stakeInputError && !!bet.stake)
    );
};

export const totalBetCountSelector = (state: SbkBetSlip) => {
    return validBetsSelector(state).filter(bet => bet?.stake && bet?.stake > 0).length;
};

export const selectionCountSelector = (state: SbkBetSlip) => Object.values(state.selections).length;

export const eventSelector = (eventId: string) => (state: SbkBetSlip) => state.events[eventId];

export const selectionSelector = (selectionId: string) => (state: SbkBetSlip) => state.selections[selectionId];

export const maxSingleOddsSelector = (state: SbkBetSlip): number => {
    return Object.values(state.options).reduce((maxOdds, option) => {
        const market = state.markets[option.marketId];
        if (
            isOptionOpen(option.status, isProducerDown(state.producerStatuses, market?.supplier), market?.published) &&
            option.odds > maxOdds
        ) {
            return option.odds;
        }
        return maxOdds;
    }, 1);
};

export const isBetSlipValidSelector = (state: SbkBetSlip) => {
    const hasStake = validBetsSelector(state).some(bet => bet?.stake && bet?.stake > 0);
    if (!hasStake) {
        return false;
    }
    const hasTotalStakeError = state.totalStakeErrors.length > 0;
    const hasStakeInputError = validBetsSelector(state).some(bet => !!bet.stakeInputError && !!bet.stake);
    return !hasTotalStakeError && !hasStakeInputError && !state.isSgpFetching;
};

export const orderedSelectionIdsSelector = (eventId: string) => (state: SbkBetSlip) => {
    return Object.keys(state.selections)
        .filter(selectionId => state.selections[selectionId].eventId === eventId)
        .sort((a: string, b: string) => state.selectionOrder.indexOf(a) - state.selectionOrder.indexOf(b));
};

export const isOddsBetrBucksSelector = (betId: string) => (state: SbkBetSlip) => isBetBetrBucks(betId, state);

export const showOddsChangeButtonSelector = (state: SbkBetSlip) => {
    const { userSettings = defaultUserSettings } = state;
    if (userSettings?.accept_multiplier_changes === AcceptMultiplierChanges.ALL) {
        return false;
    }
    if (userSettings?.accept_multiplier_changes === AcceptMultiplierChanges.HIGHER) {
        // Show when the odds decreased
        const isSingleBetOddsDecreased = Object.keys(state.oddsChanges).some(id => {
            const prevOdds = state.oddsChanges[id];
            const currentOdds = state.options[id]?.odds;
            return prevOdds > currentOdds;
        });

        const selectionIdsByEvent = groupSelectionIdsByEvent(state);
        const isSgpBetOddsDecreased = Object.entries(selectionIdsByEvent).some(([eventId, ids]) => {
            const spgId = generateSgpOddsId(ids, eventId);
            const currentOdds = state.sgpOdds[spgId];
            const previousOdds = state.sgpOddsChanges[spgId];
            if (currentOdds && previousOdds && currentOdds < previousOdds) {
                return true;
            }
        });
        return isSingleBetOddsDecreased || isSgpBetOddsDecreased;
    }
    return (
        (Object.keys(state.sgpOddsChanges).length > 0 || Object.keys(state.oddsChanges).length > 0) &&
        !state.totalStakeErrors.length
    );
};

export const comboOddsSelector = (state: SbkBetSlip) => getComboOdds(state);

export const previousComboOddsSelector = (state: SbkBetSlip) => getComboOdds(state, true);

export const hasComboOddsChangedSelector = (state: SbkBetSlip) => {
    const comboBetType = getComboBetType(state);
    if (comboBetType === ComboBetTypes.SGP) {
        return hasAnySgpOddsChanged(state);
    }
    if (comboBetType === ComboBetTypes.SGP_Plus) {
        return hasAnyComboSelectionOddsChanged(state) || hasAnySgpOddsChanged(state);
    }
    return hasAnyComboSelectionOddsChanged(state);
};

export const changedSelectionIdsSelector = (state: SbkBetSlip) =>
    state.selectionOrder.filter(id => !!state.oddsChanges[id]);

/**
 * Returns ordered selection IDs that are combo-enabled for an event
 */
export const enabledSelectionIdsSelector = (eventId: string) => (state: SbkBetSlip) =>
    Object.keys(state.selections)
        .filter(id => state.selections[id].eventId === eventId && state.selections[id].isComboEnabled)
        .sort((a, b) => state.selectionOrder.indexOf(a) - state.selectionOrder.indexOf(b));

/**
 * Returns ordered selection IDs that are NOT combo-enabled for an event
 */
export const disabledSelectionIdsSelector = (eventId: string) => (state: SbkBetSlip) =>
    Object.keys(state.selections)
        .filter(id => state.selections[id].eventId === eventId && !state.selections[id].isComboEnabled)
        .sort((a, b) => state.selectionOrder.indexOf(a) - state.selectionOrder.indexOf(b));

export const selectedPromoSelector = (betId: string) => (state: SbkBetSlip) => state.selectedPromos[betId] ?? null;
